import { hexToRgba } from '@/shared/lib/helpers/hex-to-rgba.helper';

import * as RADIUS from '../../styles/radiuses';
import * as COLOR from './colors';
import { yachtTheme as theme } from './theme';

export const yachtLightTheme = {
	colors: {
		primary: COLOR.NAVY_2,
		secondary: COLOR.WHITE,
		tertiary: COLOR.GRAY_1,
		body: COLOR.WHITE,
		metaColor: COLOR.NAVY_10,
		...theme.colors,
	},
	input: {
		primary: {
			border: COLOR.GRAY_15,
			fontColor: COLOR.GRAY_20,
			placeholderColor: COLOR.GRAY_20,
			background: hexToRgba(COLOR.BLACK, 0.05),
			focus: {
				border: COLOR.NAVY_9,
				color: COLOR.GRAY_19,
				placeholderColor: COLOR.GRAY_19,
			},
			filled: {
				color: COLOR.GRAY_19,
			},
			disabled: {
				border: hexToRgba(COLOR.WHITE, 0),
				color: COLOR.GRAY_18,
				placeholderColor: COLOR.GRAY_18,
			},
			error: {
				border: COLOR.RED_2,
				background: COLOR.RED_5,
				color: COLOR.RED_2,
				placeholderColor: COLOR.RED_2,
			},
			radius: RADIUS.PX_8,
		},
		secondary: {
			border: COLOR.GRAY_15,
			fontColor: COLOR.GRAY_20,
			placeholderColor: COLOR.GRAY_20,
			background: COLOR.WHITE,
			focus: {
				border: COLOR.NAVY_9,
				color: COLOR.GRAY_19,
				placeholderColor: COLOR.GRAY_19,
			},
			filled: {
				color: COLOR.GRAY_19,
			},
			disabled: {
				border: hexToRgba(COLOR.WHITE, 0),
				background: hexToRgba(COLOR.BLACK, 0.05),
				color: COLOR.GRAY_18,
				placeholderColor: COLOR.GRAY_18,
			},
			error: {
				border: COLOR.RED_2,
				background: COLOR.RED_5,
				color: COLOR.RED_2,
				placeholderColor: COLOR.RED_2,
			},
			radius: RADIUS.PX_8,
		},
		...theme.input,
	},
	textarea: {
		primary: {
			border: COLOR.GRAY_8,
			fontColor: COLOR.GRAY_4,
			placeholderColor: COLOR.GRAY_6,
			background: COLOR.WHITE,
			focus: {
				border: COLOR.NAVY_9,
				color: COLOR.GRAY_4,
				placeholderColor: COLOR.GRAY_6,
			},
			filled: {
				color: COLOR.GRAY_4,
			},
			radius: RADIUS.PX_6,
		},
		secondary: {
			border: COLOR.GRAY_8,
			fontColor: COLOR.GRAY_6,
			placeholderColor: COLOR.GRAY_6,
			background: COLOR.DARK_6,
			focus: {
				border: COLOR.NAVY_9,
				color: COLOR.GRAY_6,
				placeholderColor: COLOR.GRAY_6,
			},
			filled: {
				color: COLOR.GRAY_6,
			},
			radius: RADIUS.PX_6,
		},
		...theme.textarea,
	},
	box: {
		border: COLOR.GRAY_9,
		background: COLOR.GRAY_11,
	},
	checkbox: {
		background: hexToRgba(COLOR.WHITE, 0.05),
		border: COLOR.GRAY_21,
		checked: {
			background: COLOR.NAVY_9,
			border: COLOR.NAVY_9,
		},
	},
	logo: {
		fill: COLOR.NAVY_8,
		stroke: COLOR.NAVY_2,
	},
	page: {
		...theme.page,
		wrapper: {
			background: COLOR.WHITE,
			border: `1px solid ${COLOR.GRAY_9}`,
		},
	},
	text: {
		size: {
			...theme.text.size,
		},
		colors: {
			primary: '#080D17',
			secondary: COLOR.NAVY_10,
			tertiary: '#1F4885',
			invertedPrimary: COLOR.WHITE,
			invertedSecondary: COLOR.NAVY_4,
			invertedTertiary: COLOR.GRAY_3,
			inherit: 'inherit',
			...theme.colors,
		},
	},
	chip: {
		primary: {
			background: COLOR.NAVY_8,
			borderColor: COLOR.NAVY_4,
			color: COLOR.NAVY_10,
			padding: '7px 12px',
			borderRadius: '10px',
		},
		secondary: {
			background: COLOR.NAVY_10,
			borderColor: COLOR.NAVY_2,
			color: COLOR.NAVY_3,
			padding: '5px 10px',
			borderRadius: '999px',
		},
	},
	download: {
		hover: COLOR.GRAY_10,
	},
	button: {
		...theme.button,
		error: {
			background: COLOR.RED_2,
			border: '1px solid transparent',
			color: COLOR.RED_3,

			'&:hover': {
				background: COLOR.RED_4,
				color: COLOR.RED_3,
			},

			'&:active': {
				background: COLOR.RED_2,
				color: COLOR.RED_3,
			},

			'&:disabled': {
				background: hexToRgba(COLOR.BLACK, 0.05),
				color: COLOR.GRAY_18,
				cursor: 'initial',
			},
		},
		contained: {
			background: COLOR.NAVY_2,
			border: '1px solid transparent',
			color: COLOR.WHITE,

			'&:hover': {
				background: COLOR.NAVY_1,
			},

			'&:active': {
				background: COLOR.NAVY_2,
			},

			'&:disabled': {
				background: hexToRgba(COLOR.BLACK, 0.05),
				color: COLOR.GRAY_18,
				cursor: 'initial',
			},
		},
		outlined: {
			background: 'transparent',
			color: COLOR.NAVY_2,
			border: `1.5px solid ${COLOR.NAVY_2}`,

			'&:hover': {
				border: `1.5px solid ${COLOR.NAVY_1}`,
				color: COLOR.NAVY_1,
			},

			'&:active': {
				background: 'transparent',
				border: `1.5px solid ${COLOR.NAVY_2}`,
				color: COLOR.NAVY_2,
			},

			'&:disabled': {
				background: hexToRgba(COLOR.BLACK, 0.05),
				border: `1.5px solid ${hexToRgba(COLOR.BLACK, 0.05)}`,
				color: COLOR.GRAY_18,
			},
		},
		tertiary: {
			background: 'transparent',
			color: COLOR.NAVY_2,
			border: 'none',

			'&:hover': {
				background: hexToRgba(COLOR.BLACK, 0.05),
				color: COLOR.NAVY_1,
			},

			'&:active': {
				background: hexToRgba(COLOR.BLACK, 0.05),
				color: COLOR.NAVY_1,
			},

			'&:disabled': {
				background: hexToRgba(COLOR.BLACK, 0.05),
				color: COLOR.GRAY_18,
				cursor: 'initial',
			},
		},
	},
	title: {
		colors: {
			primary: COLOR.GRAY_2,
			secondary: COLOR.WHITE,
		},
		...theme.title,
	},
	toggle: {
		background: hexToRgba(COLOR.NAVY_9, 0.1),
		border: hexToRgba(COLOR.NAVY_9, 0.1),
		fill: COLOR.NAVY_9,
		checked: {
			background: hexToRgba(COLOR.NAVY_9, 0.1),
		},
		moon: {
			color: COLOR.NAVY_9,
			checked: COLOR.NAVY_9,
		},
		sun: COLOR.WHITE,
	},
	link: {
		color: COLOR.WHITE,
		active: {
			background: COLOR.NAVY_8,
			hover: COLOR.WHITE,
			colors: {
				primary: COLOR.NAVY_2,
				secondary: COLOR.NAVY_2,
			},
		},
	},
	avatar: {
		...theme.avatar,
	},
	layouts: {
		header: {
			...theme.layouts.header,
			background: COLOR.WHITE,
			borderBottom: `1px solid ${COLOR.GRAY_9}`,
			icon: {
				primary: COLOR.NAVY_2,
			},
		},
		sidebar: {
			...theme.layouts.sidebar,
			background: COLOR.SIDEBAR,
			borderRight: `1px solid ${COLOR.GRAY_9}`,
			borderBottom: `1px solid ${COLOR.GRAY_10}`,
			toggle: {
				fill: COLOR.SIDEBAR,
				stroke: COLOR.WHITE,
			},
		},
	},
	isDark: false,
	menu: {
		background: COLOR.WHITE,
		hover: COLOR.GRAY_10,
		border: `1px solid ${COLOR.GRAY_9}`,
	},
	stepper: {
		colors: {
			primary: COLOR.NAVY_2,
			disabled: COLOR.GRAY_5,
		},
		border: {
			primary: COLOR.NAVY_2,
			disabled: COLOR.GRAY_9,
		},
	},
	autocomplete: {
		...theme.autocomplete,
	},
	datepicker: {
		color: {
			primary: COLOR.GRAY_19,
			disabled: COLOR.GRAY_18,
			scheme: 'light',
		},
		selected: { background: COLOR.NAVY_9 },
		hover: COLOR.GRAY_10,
		...theme.datepicker,
	},
	leaflet: {
		color: {
			primary: COLOR.DARK_1,
		},
	},
	option: {
		background: COLOR.WHITE,
		hover: COLOR.GRAY_10,
		border: `1px solid ${COLOR.GRAY_9}`,
		color: COLOR.GRAY_19,
		selectedColor: COLOR.NAVY_9,
	},
	select: {
		fill: {
			default: COLOR.GRAY_20,
			disabled: COLOR.GRAY_18,
			error: COLOR.RED_4,
		},
		...theme.select,
	},
	zIndex: {
		...theme.zIndex,
	},
	tabs: {
		default: {
			color: COLOR.GRAY_20,
			activeColor: COLOR.NAVY_2,
			// TODO: Replace the legacy Tabs with Tabs from hdata-ui and remove this temporary page background token
			background: 'var(--hdata-background)',
			activeBackground: 'transparent',
			border: `1px solid ${COLOR.GRAY_9}`,
			...theme.tabs.default,
		},
		pill: {
			color: COLOR.GRAY_20,
			activeColor: COLOR.NAVY_2,
			background: 'transparent',
			activeBackground: COLOR.WHITE,
			border: 'none',
			...theme.tabs.pill,
		},
	},
	table: {
		headColor: COLOR.NAVY_2,
		bodyColor: COLOR.GRAY_14,
		background: COLOR.GRAY_16,
		measurementBackground: COLOR.NAVY_8,
		zebraBackground: hexToRgba(COLOR.BLACK, 0.05),
		headBackground: COLOR.GRAY_17,
		border: `1px solid ${COLOR.GRAY_15}`,
		rowBorder: 'none',
		offlineBackground: COLOR.GRAY_10,
		...theme.table,
	},
	toast: {
		background: COLOR.WHITE,
		closeColor: COLOR.GRAY_3,
	},
	caption: {
		primary: COLOR.GRAY_2,
		secondary: COLOR.GRAY_4,
		tertiary: COLOR.GRAY_5,
		gray: COLOR.GRAY_11,
		dark: COLOR.GRAY_5,
	},
	tag: {
		border: COLOR.NAVY_4,
		radius: RADIUS.PX_5,
		background: COLOR.NAVY_8,
		color: COLOR.NAVY_2,
		...theme.tag,
	},
	list: {
		color: COLOR.GRAY_2,
	},
	container: {
		border: COLOR.GRAY_9,
		radius: RADIUS.PX_2,
	},
	section: {
		border: COLOR.GRAY_9,
		color: COLOR.GRAY_6,
	},
	breadcrumbs: {
		color: COLOR.NAVY_2,
		background: COLOR.NAVY_8,
		border: 'none',
		radius: RADIUS.PX_2,
	},
	transition: {
		...theme.transition,
	},
	modal: {
		border: COLOR.GRAY_10,
		borderInside: COLOR.GRAY_10,
		background: COLOR.WHITE,
		backgroundActions: COLOR.GRAY_11,
		backgroundHeader: COLOR.NAVY_8,
		icon: COLOR.GRAY_3,
		color: COLOR.NAVY_2,
		radius: RADIUS.PX_8,
	},
	groupManagement: {
		...theme.groupManagement,
		groupCard: {
			...theme.groupManagement.groupCard,
			background: COLOR.WHITE,
			borderColor: COLOR.GRAY_9,
			headerBackground: COLOR.NAVY_8,
			rowBackground: 'transparent',
			rowBorderColor: COLOR.GRAY_9,
			metricChipBackground: COLOR.NAVY_8,
		},
	},
	pagination: {
		color: COLOR.GRAY_5,
		background: 'transparent',
		border: `1px solid ${COLOR.GRAY_9}`,
		active: {
			background: COLOR.NAVY_8,
			color: COLOR.NAVY_2,
		},
	},
	tooltip: {
		background: COLOR.WHITE,
		hover: COLOR.GRAY_10,
		border: `1px solid ${COLOR.GRAY_9}`,
		color: {
			primary: COLOR.GRAY_1,
			secondary: COLOR.GRAY_5,
		},
	},
	widgets: {
		circle: { color: COLOR.NAVY_2, background: COLOR.NAVY_8 },
		arrow: COLOR.GRAY_1,
		addSection: {
			background: COLOR.WHITE,
		},
	},
	breakpoints: {
		...theme.breakpoints,
	},
	media: {
		...theme.media,
	},
	radio: {
		border: COLOR.GRAY_21,
		background: COLOR.NAVY_9,
		active: COLOR.WHITE,
	},
	spinner: {
		...theme.spinner,
		color: {
			primary: COLOR.NAVY_2,
			secondary: COLOR.WHITE,
		},
	},
	map: {
		icon: COLOR.GRAY_1,
	},
	windy: {
		icon: {
			background: COLOR.LIME_1,
			fill: {
				active: COLOR.DARK_1,
				unactive: COLOR.WHITE,
			},
			...theme.windy.icon,
		},
		buttons: {
			background: COLOR.GRAY_12,
			...theme.windy.buttons,
		},
	},
	notification: {
		icon: {
			backgroundColor: COLOR.NAVY_8,
			activeColor: COLOR.NAVY_2,
		},
		card: {
			activeBackground: COLOR.NAVY_8,
			defaultBorder: 'none',
			border: 'none',
		},
		menu: {
			icon: COLOR.WHITE,
			mobile: {
				icon: COLOR.GRAY_1,
			},
		},
	},
	icon: {
		roundWrapper: {
			primary: {
				backgroundColor: COLOR.NAVY_8,
				fill: COLOR.NAVY_2,
			},
			secondary: {
				backgroundColor: hexToRgba(COLOR.WHITE, 0.1),
				fill: COLOR.WHITE,
			},
		},
	},
	skeleton: {
		gradient: `linear-gradient(to right, ${COLOR.NAVY_1} 0%, ${COLOR.NAVY_2} 50%, ${COLOR.NAVY_3} 100%)`,
		shadow: COLOR.NAVY_8,
	},
	marker: {
		primary: COLOR.NAVY_2,
		secondary: COLOR.GRAY_2,
		error: COLOR.ERROR,
	},
};
